import { action, makeObservable, observable, runInAction, when } from 'mobx';

import type { CurvesCacheService, CurvePoint } from '../api/CurvesCacheService';
import type { LogCurve, WellIndexType } from '../api/IWellDirectoryService';
import type { WellDirectoryService } from '../api/WellDirectoryService';
import type { BaseParameterTypes } from '../baseParams';
import type { SocketCurveMessage, WellLogWidgetStompPublisher } from '../stompTypes';
import { hasValue } from '../utils';

type CurvesDataMap = Partial<Record<BaseParameterTypes, LogCurve>>;

type BaseParamsResponse = Awaited<ReturnType<WellDirectoryService['getBaseParameters']>>;

export class CurveCoordinatesService {
  isLoading = false;

  private loadVersion = 0;

  private coordinates = new Map<number, CurvePoint[]>();

  private curveSubscriptions = new Map<number, (message: SocketCurveMessage) => void>();

  private pendingLoads = new Map<
    number,
    {
      promise: Promise<void>;
      points: Map<number, CurvePoint>;
      repeat: boolean;
    }
  >();

  constructor(
    private readonly wellDirectoryService: WellDirectoryService,
    private readonly curvesCacheService: CurvesCacheService,
    private readonly stompPublisher: WellLogWidgetStompPublisher,
    private readonly getIndex: () => WellIndexType,
    private readonly onCoordinatesChanged: (curveId: number, coordinates: CurvePoint[]) => void,
  ) {
    makeObservable<this, 'loadVersion'>(this, {
      isLoading: observable,
      loadVersion: observable,
      loadCurves: action,
      reset: action,
    });
  }

  async loadBaseParameters(
    wellboreId: number,
    getCurveById: (id: number) => LogCurve | null,
    allowedParameters: BaseParameterTypes[],
  ): Promise<{ curvesData: CurvesDataMap; params: BaseParamsResponse }> {
    const params = await this.wellDirectoryService.getBaseParameters(wellboreId, this.getIndex());

    const filteredParams = params.filter((param) => allowedParameters.includes(param.parameter));

    const curvesData = filteredParams.reduce(
      (acc, param) => {
        const curve = param.curveId ? getCurveById(param.curveId) : undefined;
        if (!curve) {
          return acc;
        }
        acc[param.parameter] = curve;
        return acc;
      },
      {} as Record<BaseParameterTypes, LogCurve>,
    );

    return { curvesData, params };
  }

  async loadCurves(curvesData: CurvesDataMap | null, refresh = false): Promise<void> {
    const version = ++this.loadVersion;
    const curveIds = new Set(this.getCurvesIds(curvesData) ?? []);
    this.isLoading = true;
    if (refresh) {
      for (const curveId of curveIds) {
        // A subscription gap requires a fresh request, even if an old one hangs.
        this.pendingLoads.delete(curveId);
      }
    }
    const superseded = when(() => version !== this.loadVersion);
    try {
      await Promise.race([Promise.all([...curveIds].map((id) => this.fetchCurveCoordinates(id))), superseded]);
      if (version === this.loadVersion) {
        runInAction(() => {
          this.isLoading = false;
        });
      }
    } catch (error) {
      if (version === this.loadVersion) {
        // The caller sets failed status and resets the service before clearing loading.
        throw error;
      }
    } finally {
      superseded.cancel();
    }
  }

  updateSubscriptions(curvesData: CurvesDataMap | null) {
    const targetIds = new Set(this.getCurvesIds(curvesData) ?? []);
    let addedSubscriptions = false;

    this.curveSubscriptions.forEach((handler, curveId) => {
      if (!targetIds.has(curveId)) {
        this.stompPublisher.unsubscribe(`/curve/${curveId}`, handler);
        this.curveSubscriptions.delete(curveId);
        this.pendingLoads.delete(curveId);
        this.coordinates.delete(curveId);
      }
    });

    targetIds.forEach((curveId) => {
      if (this.curveSubscriptions.has(curveId)) {
        return;
      }
      const handler = this.createCurveSocketHandler(curveId);
      this.curveSubscriptions.set(curveId, handler);
      this.stompPublisher.subscribe(`/curve/${curveId}`, handler);
      addedSubscriptions = true;
    });
    return addedSubscriptions;
  }

  unsubscribeAll() {
    this.curveSubscriptions.forEach((handler, curveId) => {
      this.stompPublisher.unsubscribe(`/curve/${curveId}`, handler);
    });
    this.curveSubscriptions.clear();
  }

  reset() {
    this.loadVersion += 1;
    this.isLoading = false;
    this.unsubscribeAll();
    this.pendingLoads.clear();
    this.coordinates.clear();
  }

  mergePointSorted(points: CurvePoint[], point: CurvePoint) {
    if (!Number.isFinite(point.key) || !Number.isFinite(point.value)) {
      return points;
    }
    let left = 0;
    let right = points.length;
    if (points.length && point.key > points[points.length - 1].key) {
      left = right;
    }
    while (left < right) {
      const mid = left + Math.floor((right - left) / 2);
      if (points[mid].key < point.key) {
        left = mid + 1;
      } else {
        right = mid;
      }
    }
    if (points[left]?.key === point.key && points[left].value === point.value) {
      return points;
    }
    const result = [...points];
    result.splice(left, points[left]?.key === point.key ? 1 : 0, point);
    return result;
  }

  mergePointsSorted(points: CurvePoint[], updates: CurvePoint[]) {
    if (updates.length === 0) {
      return points;
    }
    if (updates.length === 1) {
      return this.mergePointSorted(points, updates[0]);
    }
    const ordered = [
      ...new Map(
        updates
          .filter((point) => Number.isFinite(point.key) && Number.isFinite(point.value))
          .map((point) => [point.key, point]),
      ).values(),
    ].sort((a, b) => a.key - b.key);
    const result: CurvePoint[] = [];
    let index = 0;
    for (const point of ordered) {
      while (index < points.length && points[index].key < point.key) {
        result.push(points[index++]);
      }
      if (points[index]?.key === point.key) {
        index++;
      }
      result.push(point);
    }
    while (index < points.length) {
      result.push(points[index++]);
    }
    return result;
  }

  private createCurveSocketHandler(curveId: number) {
    const handler = (message: SocketCurveMessage) => {
      if (this.curveSubscriptions.get(curveId) !== handler) {
        return;
      }
      switch (message.msgType) {
        case 'POINT':
          this.handleCurvePointMessage(curveId, message);
          break;
        case 'LOADED': {
          const pending = this.pendingLoads.get(curveId);
          if (pending) {
            pending.repeat = true;
            pending.points.clear();
          }
          this.fetchCurveCoordinates(curveId).catch((error: unknown) => {
            if (this.curveSubscriptions.get(curveId) === handler) {
              console.error('Failed to reload curve coordinates', error);
            }
          });
          break;
        }
        default:
          break;
      }
    };
    return handler;
  }

  private handleCurvePointMessage(curveId: number, message: Extract<SocketCurveMessage, { msgType: 'POINT' }>) {
    const value = Number(message.value);
    if (!Number.isFinite(value) || !Number.isFinite(message.key)) {
      return;
    }

    const point = { key: message.key, value };
    this.pendingLoads.get(curveId)?.points.set(point.key, point);
    const coordinates = this.mergePointSorted(this.coordinates.get(curveId) ?? [], point);
    this.coordinates.set(curveId, coordinates);
    this.onCoordinatesChanged(curveId, coordinates);
  }

  private fetchCurveCoordinates(curveId: number): Promise<void> {
    const existing = this.pendingLoads.get(curveId);
    if (existing) {
      return existing.promise;
    }
    const pending = {
      points: new Map<number, CurvePoint>(),
      repeat: false,
      promise: Promise.resolve(),
    };
    this.pendingLoads.set(curveId, pending);
    pending.promise = (async () => {
      try {
        let coordinates: CurvePoint[] = [];
        do {
          pending.repeat = false;
          const response =
            this.getIndex() === 'LOG_DEPTH'
              ? await this.curvesCacheService.getDepthCoords(curveId)
              : await this.curvesCacheService.getTimeCoords(curveId);
          if (this.pendingLoads.get(curveId) !== pending) {
            return;
          }
          if (Array.isArray(response)) {
            const points = response.filter(
              (point): point is CurvePoint =>
                'key' in point && 'value' in point && Number.isFinite(point.key) && Number.isFinite(point.value),
            );
            if (points.some((point, index) => index > 0 && points[index - 1].key > point.key)) {
              points.sort((a, b) => a.key - b.key);
            }
            coordinates = points.filter((point, index) => points[index + 1]?.key !== point.key);
          }
        } while (pending.repeat && this.pendingLoads.get(curveId) === pending);
        coordinates = this.mergePointsSorted(coordinates, [...pending.points.values()]);
        this.coordinates.set(curveId, coordinates);
        this.onCoordinatesChanged(curveId, coordinates);
      } catch (error) {
        if (this.pendingLoads.get(curveId) === pending) {
          throw error;
        }
      } finally {
        if (this.pendingLoads.get(curveId) === pending) {
          this.pendingLoads.delete(curveId);
        }
      }
    })();
    return pending.promise;
  }

  private getCurvesIds(curvesData: CurvesDataMap | null): number[] | null {
    if (!curvesData) {
      return null;
    }
    return Object.values(curvesData)
      .map((curve) => curve?.goStreamId)
      .filter(hasValue);
  }
}
